try {
  /* ────────── selectors ────────── */
  const focusable = "a[href], button, textarea, input, select, [tabindex]";
  const unfocusable = '[disabled], [tabindex="-1"], [type="hidden"]';
  const focusableSelector = `:is(${focusable}):not(:is(${unfocusable}))`;

  /* ────────── state ────────── */
  let currentCmpbox = null; // the banner element we’ve wired up
  let disposeCurrent = () => {}; // noop until first init
  let lastPurpose = null; // tracks which tab was activated

  /* ────────── helpers ────────── */
  const createTrapFocus = (cmpboxEl) => {
    const focusableEls = [...cmpboxEl.querySelectorAll(focusableSelector)];
    const [firstEl] = focusableEls;
    const lastEl = focusableEls.at(-1);

    const handleTrap = (e) => {
      if (e.key !== "Tab") return;
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };

    cmpboxEl.addEventListener("keydown", handleTrap);

    /* disposer */
    return () => cmpboxEl.removeEventListener("keydown", handleTrap);
  };

  const createTabLogic = (cmpboxEl) => {
    const remember = (tabEl) => {
      lastPurpose = tabEl?.dataset.cmpPurpose ?? null;
    };

    /* delegated click + key */
    const handleClick = (e) => {
      const tabEl = e.target.closest(".cmpboxnaviitem");
      if (tabEl) remember(tabEl);
    };

    const handleKey = (e) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const tabEl = e.target.closest(".cmpboxnaviitem");
      if (!tabEl) return;

      if (e.key === " ") e.preventDefault(); // prevent page scroll
      remember(tabEl);
      tabEl.click(); // trigger CMP swap
    };

    cmpboxEl.addEventListener("click", handleClick);
    cmpboxEl.addEventListener("keydown", handleKey);

    /* internal observer focuses new clone on each swap */
    const refocusObserver = new MutationObserver(() => {
      const activeEl = cmpboxEl.querySelector(".cmpboxnaviitemactive");
      if (activeEl) {
        activeEl.focus();
        lastPurpose = null;
        return;
      }
      if (lastPurpose) {
        cmpboxEl.querySelector(`.cmpboxnaviitem[data-cmp-purpose="${lastPurpose}"]`)?.focus();
        lastPurpose = null;
      }
    });
    refocusObserver.observe(cmpboxEl, { childList: true, subtree: true });

    /* disposer */
    return () => {
      cmpboxEl.removeEventListener("click", handleClick);
      cmpboxEl.removeEventListener("keydown", handleKey);
      refocusObserver.disconnect();
    };
  };

  const initCmpbox = (cmpboxEl) => {
    /* unique init log */
    // console.log("Custom CW CMP focus-trap initializing for", cmpboxEl);

    cmpboxEl.querySelector(focusableSelector)?.focus();
    const disposeTrap = createTrapFocus(cmpboxEl);
    const disposeTabs = createTabLogic(cmpboxEl);

    /* mark so we don’t double-init the same node */
    cmpboxEl.classList.add("focus-trap-initialised");

    /* combined disposer */
    return () => {
      disposeTrap();
      disposeTabs();
      cmpboxEl.classList.remove("focus-trap-initialised");
    };
  };

  /* ────────── observer – manage banner lifecycle ────────── */
  const bodyObserver = new MutationObserver(() => {
    const newCmpbox = document.querySelector("#cmpbox");

    /* 1. banner removed → clean up */
    if (!newCmpbox && currentCmpbox) {
      disposeCurrent();
      disposeCurrent = () => {};
      currentCmpbox = null;
      lastPurpose = null;
      return;
    }

    /* 2. banner (re)appeared with a *new* element → swap in */
    if (newCmpbox && newCmpbox !== currentCmpbox) {
      /* clean previous just in case */
      disposeCurrent();
      currentCmpbox = newCmpbox;
      disposeCurrent = initCmpbox(newCmpbox);
    }
  });

  bodyObserver.observe(document.body, { childList: true, subtree: true });
} catch (err) {
  console.error("CMP focus-trap error:", err);
}
